import { useQueryClient } from '@tanstack/react-query'
import { Spin } from 'antd'
import { FaqArticlePayload } from 'Modules'
import React, { useMemo } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { normalizeRichTextHtml } from 'component/rich-text-editor/utils'
import { BREADCRUMBS } from 'constants/breadcrumb'
import {
  faqCategoryKeys,
  useFaqArticleDetail,
  useUnsavedChangesGuard,
  useUpdateFaqArticle,
} from 'hooks/faq'
import {
  buildArticleBreadcrumb,
  DiscardChangesModal,
  FaqArticleForm,
  FaqArticleFormValues,
  FaqArticleLoadError,
} from 'modules/faq'
import { PAGES } from 'routes'
import { getApiErrorCode, getApiErrorMessage } from 'utils/api-error'

import '../style.scss'

const FaqArticleEditPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: article, isLoading, error, refetch } = useFaqArticleDetail(id)
  const updateArticle = useUpdateFaqArticle()
  const { setDirty, requestLeave, discardModalProps } = useUnsavedChangesGuard()

  const detailPath = generatePath(PAGES.FAQ_ARTICLE_DETAIL, { id })

  const initialValues = useMemo<FaqArticleFormValues | undefined>(
    () =>
      article
        ? {
            title: article.title,
            // compare against the editor's own serialization, not the raw stored HTML
            content: normalizeRichTextHtml(article.content),
            categoryId: article.categoryId,
            published: article.status === 'PUBLISHED',
          }
        : undefined,
    [article]
  )

  const handleSubmit = async (payload: FaqArticlePayload) => {
    try {
      const updated = await updateArticle.mutateAsync({ id, payload })
      if (updated?.movedToCategory) {
        toast.success(`Article moved to ${updated.movedToCategory.name}`)
      } else {
        toast.success('Save changes successfully')
      }
      navigate(detailPath, { replace: true })
    } catch (err: unknown) {
      if (getApiErrorCode(err) === 'FAQ_ARTICLE_CATEGORY_FULL') {
        toast.error('This category already has 20 articles')
        void queryClient.invalidateQueries({ queryKey: faqCategoryKeys.lists() })
      } else {
        toast.error(getApiErrorMessage(err, 'Failed to save article'))
      }
    }
  }

  const breadcrumbItems = article
    ? [BREADCRUMBS.FAQ, buildArticleBreadcrumb(article.title)]
    : [BREADCRUMBS.FAQ]

  return (
    <div className="main-section faq-management-page faq-article-page">
      <Breadcrumb additionalItems={breadcrumbItems} onNavigate={requestLeave} />
      <div className="main-title">Edit Article</div>

      {isLoading ? (
        <div className="faq-article-loading">
          <Spin />
        </div>
      ) : article && initialValues ? (
        <FaqArticleForm
          key={article._id}
          initialValues={initialValues}
          submitting={updateArticle.isPending}
          onSubmit={(payload) => void handleSubmit(payload)}
          onCancel={() => requestLeave(detailPath, { replace: true })}
          onDirtyChange={setDirty}
        />
      ) : (
        <FaqArticleLoadError
          error={error}
          onBack={() => requestLeave(PAGES.FAQ)}
          onRetry={() => void refetch()}
        />
      )}

      <DiscardChangesModal {...discardModalProps} />
    </div>
  )
}

export default FaqArticleEditPage
